import React, { useMemo } from "react";
import {
    CartesianGrid,
    Label,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { BarChart as BarChartIcon } from "@mui/icons-material";

import * as Styled from "./ReportChart.styled";
import { Spinner } from "react-bootstrap";
import { getChartData } from "./ReportChart.helpers";
import { clearDupplicates } from "../../../helpers/tools";

const ReportChart = ({ stats, isLoading, isSingularDay, hourlyStats }) => {
    const colors = ["#0066FF", "#FF0000", "#FFA800", "#F66700", "#45C300"];

    // Check if we should use hourly data
    const hasHourlyData = hourlyStats && hourlyStats.length > 0;

    // dupplicate leri sil
    const pageNames = clearDupplicates(
        stats
            ?.filter((stat) => stat?.page_name)
            ?.map((stat) => stat?.page_name),
    );
    const formNames = clearDupplicates(
        stats
            ?.filter((stat) => stat?.form_name)
            ?.map((stat) => stat?.form_name),
    );
    const sourceName = stats?.find((stat) => stat?.name)?.name;

    const statDates = useMemo(
        () =>
            stats?.reduce((dates, stat) => {
                const sentAt = stat?.successfully_sent_at;

                !dates.find((date) => sentAt === date) && dates.push(sentAt);
                return dates;
            }, []),
        [stats],
    );
    const data = useMemo(() => {
        const commonPayload = {
            formNames,
            pageNames,
            sourceName,
            statDates,
            stats,
            returnDate: !isSingularDay,
        };
        return isSingularDay
            ? getChartData(commonPayload)
            : getChartData(commonPayload);
    }, [stats, isSingularDay, formNames, pageNames, sourceName, statDates]);

    // Check if data is empty - consider hourly data as well
    const isEmpty =
        !isLoading &&
        (hasHourlyData
            ? false // If we have hourly data, it's not empty
            : !data ||
              data.length === 0 ||
              data.every((item) =>
                  Object.values(item).every(
                      (value) =>
                          value === null ||
                          value === undefined ||
                          value === 0 ||
                          value === "",
                  ),
              ));

    // Tooltip for hourly chart
    const HourlyTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            return (
                <div className="custom-chart-tooltip">
                    <p
                        className="label"
                        style={{ fontWeight: 600, marginBottom: 4 }}
                    >
                        {data?.date} - {data?.hour}
                    </p>
                    <p className="label">Leads: {data?.count}</p>
                </div>
            );
        }
        return null;
    };

    // Legend for hourly bar chart
    const HourlyLegend = () => (
        <div className="chart-legend-list">
            <div>
                <span
                    className="square"
                    style={{ backgroundColor: "#6366F1" }}
                ></span>
                <span className="title">Leads</span>
            </div>
        </div>
    );

    const CustomTooltip = ({ active, payload }) => {
        if (active) {
            const line = payload?.[0];
            const formValues =
                formNames?.map((formName, index) => ({
                    key: `form_${index}`,
                    label: formName,
                    value: line?.payload?.[`form_${index}`],
                })) || [];
            const pageValues =
                pageNames?.map((pageName, index) => ({
                    key: `page_${index}`,
                    label: pageName,
                    value: line?.payload?.[`page_${index}`],
                })) || [];

            const nameValues = [
                {
                    key: "name",
                    label: sourceName,
                    value: line?.payload?.name,
                },
            ];

            const combinedValues = [
                ...formValues,
                ...pageValues,
                ...nameValues,
            ];

            return (
                <div className="custom-chart-tooltip">
                    {combinedValues?.map((combinedValue) => {
                        if (combinedValue?.value !== undefined)
                            return (
                                <p className="label">{`${combinedValue?.label} : ${combinedValue?.value}`}</p>
                            );
                        return null;
                    })}
                </div>
            );
        }

        return null;
    };

    const CustomLegend = ({ payload }) => {
        return (
            <div className="chart-legend-list">
                {payload &&
                    payload.length > 0 &&
                    payload.map((entry, index) => (
                        <div
                            data-key={entry?.payload?.label}
                            key={`item-${index}`}
                        >
                            <span
                                data-color={colors[index]}
                                className="square"
                            ></span>
                            <span className="title">
                                {entry?.payload?.label}
                            </span>
                        </div>
                    ))}
            </div>
        );
    };

    // Empty state component
    const EmptyState = () => (
        <Box
            className="empty-state-container"
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
                minHeight: "300px",
                padding: "40px 20px",
                textAlign: "center",
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
            }}
        >
            <Box
                className="empty-state-icon"
                sx={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                }}
            >
                <BarChartIcon sx={{ fontSize: 40, color: "#999" }} />
            </Box>
            <Typography
                className="empty-state-title"
                variant="h6"
                sx={{
                    color: "#666",
                    fontWeight: 500,
                    marginBottom: "8px",
                }}
            >
                No Data Available
            </Typography>
            <Typography
                className="empty-state-description"
                variant="body2"
                sx={{
                    color: "#999",
                    maxWidth: "300px",
                    lineHeight: 1.5,
                }}
            >
                There's no data to display in the chart. Data will appear here
                once leads are generated.
            </Typography>
        </Box>
    );

    return (
        <Styled.ChartContainer>
            {isLoading && (
                <Spinner
                    animation="border"
                    role="status"
                    variant="dark"
                    className="p-5 chart-loader"
                />
            )}
            {isEmpty ? (
                <EmptyState />
            ) : hasHourlyData ? (
                // Hourly Line Chart - shown when hourly data is available
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        width={500}
                        height={300}
                        data={hourlyStats}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} domain={[0, "auto"]} />
                        <Tooltip content={<HourlyTooltip />} />
                        <Legend content={<HourlyLegend />} />
                        <Line
                            type="monotone"
                            dataKey="count"
                            stroke={colors[4]}
                            name="Leads"
                            dot={{ fill: colors[4], strokeWidth: 2 }}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                // Daily Line Chart - shown when hourly data is not available
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} domain={[0, "auto"]} />
                        <Tooltip content={CustomTooltip} />
                        <Legend content={<CustomLegend />} />
                        {pageNames?.map((pageName, index) => {
                            return (
                                <Line
                                    type="monotone"
                                    dataKey={`page_${index}`}
                                    stroke={colors[index]}
                                    label={pageName}
                                >
                                    <Label
                                        value={pageName}
                                        offset={0}
                                        position="insideBottom"
                                    />
                                </Line>
                            );
                        })}
                        {formNames?.map((formName, index) => {
                            return (
                                <Line
                                    type="monotone"
                                    dataKey={`form_${index}`}
                                    stroke={colors[index]}
                                    label={formName}
                                ></Line>
                            );
                        })}
                        {sourceName && (
                            <Line
                                type="monotone"
                                dataKey={`name`}
                                stroke={colors[4]}
                                label={sourceName}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </Styled.ChartContainer>
    );
};

export default ReportChart;
